import React, { useState, useEffect } from 'react';
import { Users, Search, Filter, Mail, Download, FileText, UserCheck, Shield, MessageSquare, TrendingUp, Activity, AlertCircle, CheckCircle, UserX, UserPlus, ArrowLeft, Award, Coins, X, ChevronRight, RefreshCw, Trash2 } from 'lucide-react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import { getUserManagementAnalytics, getUserGrowthTrend, getUserTableData, toggleUserAccountStatus, getRolePermissionsData, getCommunityStats, sendPasswordResetEmail, exportUserDataAsCSV, deleteUserAccount } from '../../services/userManagementService';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import OnboardingEmailModal from './components/OnboardingEmailModal';




// Mock user data
const mockUserData = {
  users: [
    { id: 1, username: 'john_doe', email: 'john@example.com', status: 'active', role: 'standard', joinDate: '2024-01-15', lastActive: '2 hours ago', versesMemorized: 145, streak: 28, engagement: 'high' },
    { id: 2, username: 'sarah_smith', email: 'sarah@example.com', status: 'active', role: 'premium', joinDate: '2024-02-20', lastActive: '1 day ago', versesMemorized: 89, streak: 15, engagement: 'medium' },
    { id: 3, username: 'mike_johnson', email: 'mike@example.com', status: 'inactive', role: 'standard', joinDate: '2023-11-10', lastActive: '2 weeks ago', versesMemorized: 234, streak: 0, engagement: 'low' },
    { id: 4, username: 'emma_wilson', email: 'emma@example.com', status: 'active', role: 'beta_tester', joinDate: '2024-03-05', lastActive: '5 minutes ago', versesMemorized: 312, streak: 45, engagement: 'high' },
    { id: 5, username: 'david_brown', email: 'david@example.com', status: 'suspended', role: 'standard', joinDate: '2024-01-28', lastActive: '1 month ago', versesMemorized: 56, streak: 0, engagement: 'low' },
    { id: 6, username: 'lisa_garcia', email: 'lisa@example.com', status: 'active', role: 'premium', joinDate: '2023-12-12', lastActive: '3 hours ago', versesMemorized: 198, streak: 32, engagement: 'high' },
    { id: 7, username: 'james_martinez', email: 'james@example.com', status: 'active', role: 'standard', joinDate: '2024-02-14', lastActive: '1 week ago', versesMemorized: 67, streak: 8, engagement: 'medium' },
    { id: 8, username: 'olivia_davis', email: 'olivia@example.com', status: 'active', role: 'admin', joinDate: '2023-10-01', lastActive: '30 minutes ago', versesMemorized: 421, streak: 67, engagement: 'high' }
  ],
  analytics: {
    totalUsers: 1247,
    activeUsers: 892,
    newUsersThisMonth: 143,
    averageEngagement: 68.5,
    userGrowth: [
      { month: 'Jan', users: 850 },
      { month: 'Feb', users: 920 },
      { month: 'Mar', users: 1050 },
      { month: 'Apr', users: 1120 },
      { month: 'May', users: 1180 },
      { month: 'Jun', users: 1247 }
    ],
    engagementDistribution: [
      { name: 'High', value: 425, color: 'var(--color-success)' },
      { name: 'Medium', value: 467, color: 'var(--color-warning)' },
      { name: 'Low', value: 355, color: 'var(--color-destructive)' }
    ],
    roleDistribution: [
      { role: 'Standard', count: 892 },
      { role: 'Premium', count: 234 },
      { role: 'Beta Tester', count: 89 },
      { role: 'Admin', count: 32 }
    ]
  },
  permissions: [
    { id: 1, role: 'Standard', users: 892, canImport: false, canExport: true, canModerate: false, canAccessAdmin: false },
    { id: 2, role: 'Premium', users: 234, canImport: true, canExport: true, canModerate: false, canAccessAdmin: false },
    { id: 3, role: 'Beta Tester', users: 89, canImport: true, canExport: true, canModerate: true, canAccessAdmin: false },
    { id: 4, role: 'Admin', users: 32, canImport: true, canExport: true, canModerate: true, canAccessAdmin: true }
  ],
  communityStats: {
    totalReflections: 3456,
    publicProfiles: 892,
    flaggedContent: 12,
    moderationActions: 45
  }
};

const UserManagement = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [engagementFilter, setEngagementFilter] = useState('all');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [analytics, setAnalytics] = useState({
    totalUsers: 0,
    activeUsers: 0,
    newUsersThisMonth: 0,
    averageEngagement: 0
  });
  const [loading, setLoading] = useState(true);
  const [userGrowthData, setUserGrowthData] = useState([]);
  const [userLoading, setUserLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [pagination, setPagination] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [permissionsData, setPermissionsData] = useState([]);
  const [communityStats, setCommunityStats] = useState({
    totalReflections: 0,
    publicProfiles: 0,
    flaggedContent: 0,
    moderationActions: 0
  });
  const [communityLoading, setCommunityLoading] = useState(true);
  const { user: adminUser } = useAuth();

  // Award Talents state
  const [awardSearch, setAwardSearch] = useState('');
  const [awardSearchResults, setAwardSearchResults] = useState([]);
  const [awardSearchLoading, setAwardSearchLoading] = useState(false);
  const [selectedAwardUser, setSelectedAwardUser] = useState(null);
  const [goldAmount, setGoldAmount] = useState(0);
  const [silverAmount, setSilverAmount] = useState(0);
  const [awardNote, setAwardNote] = useState('');
  const [awardLoading, setAwardLoading] = useState(false);
  const [awardSuccess, setAwardSuccess] = useState('');
  const [awardError, setAwardError] = useState('');
  const [recentGrants, setRecentGrants] = useState([]);
  const [grantsLoading, setGrantsLoading] = useState(false);

  // Quick Actions state
  const [quickActionLoading, setQuickActionLoading] = useState('');
  const [quickActionMessage, setQuickActionMessage] = useState({ type: '', text: '' });

  // Manage Roles modal state
  const [showManageRoles, setShowManageRoles] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleUpdateLoading, setRoleUpdateLoading] = useState(false);
  const [roleUpdateMessage, setRoleUpdateMessage] = useState('');

  // View All Activity modal state
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [activityLog, setActivityLog] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);

  // Bulk Operations modal state
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkMessage, setBulkMessage] = useState({ type: '', text: '' });

  // Password Reset modal state
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState({ type: '', text: '' });

  // Activity Reports modal state
  const [showReportsModal, setShowReportsModal] = useState(false);

  // Onboarding Email modal state
  const [showOnboardingEmailModal, setShowOnboardingEmailModal] = useState(false);

  // Delete confirmation modal state
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, userId: null, username: '', step: 1 });

  // Fetch real analytics data on component mount
  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);
      try {
        const data = await getUserManagementAnalytics();
        setAnalytics(data);
        const growthData = await getUserGrowthTrend();
        setUserGrowthData(growthData);
      } catch (error) {
        console.error('Error fetching analytics:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, []);

  // Fetch user table data with pagination
  useEffect(() => {
    const fetchUsers = async () => {
      setUserLoading(true);
      try {
        const data = await getUserTableData(currentPage, pageSize);
        setUsers(data?.users || []);
        setPagination(data?.pagination || null);
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setUserLoading(false);
      }
    };
    fetchUsers();
  }, [currentPage, pageSize]);

  // Fetch permissions & community data
  useEffect(() => {
    const fetchAdminData = async () => {
      setCommunityLoading(true);
      try {
        const [perms, community] = await Promise.all([
          getRolePermissionsData(),
          getCommunityStats()
        ]);
        setPermissionsData(perms);
        setCommunityStats(community);
      } catch (error) {
        console.error('Error fetching admin data:', error);
      } finally {
        setCommunityLoading(false);
      }
    };
    fetchAdminData();
  }, []);

  // Handle enable/disable user account
  const handleToggleUserStatus = async (userId, currentStatus, username) => {
    const newStatus = !currentStatus;
    const action = newStatus ? 'enable' : 'disable';
    if (!window.confirm(`Are you sure you want to ${action} the account for ${username}?`)) return;

    setActionLoading(prev => ({ ...prev, [userId]: true }));
    try {
      const result = await toggleUserAccountStatus(userId, newStatus);
      if (result?.success) {
        setUsers(prevUsers =>
          prevUsers?.map(user =>
            user?.id === userId ? { ...user, isActive: newStatus } : user
          )
        );
        alert(result?.message || `Account ${action}d successfully`);
      } else {
        alert(result?.error || `Failed to ${action} account`);
      }
    } catch (error) {
      console.error('Error toggling user status:', error);
      alert(`Failed to ${action} account. Please try again.`);
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: false }));
    }
  };

  // Handle delete user account
  const handleDeleteUser = (userId, username) => {
    setDeleteConfirm({ show: true, userId, username, step: 1 });
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirm?.step === 1) {
      setDeleteConfirm(prev => ({ ...prev, step: 2 }));
      return;
    }

    const { userId, username } = deleteConfirm;
    setDeleteConfirm(prev => ({ ...prev, show: false }));
    setActionLoading(prev => ({ ...prev, [`delete_${userId}`]: true }));
    try {
      const result = await deleteUserAccount(userId);
      if (result?.success) {
        setUsers(prevUsers => prevUsers?.filter(user => user?.id !== userId));
        setSelectedUsers(prev => prev?.filter(id => id !== userId));
        setQuickActionMessage({ type: 'success', text: `User "${username}" and all their data have been permanently deleted.` });
      } else {
        setQuickActionMessage({ type: 'error', text: result?.error || 'Failed to delete user account.' });
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      setQuickActionMessage({ type: 'error', text: 'Failed to delete user account. Please try again.' });
    } finally {
      setActionLoading(prev => ({ ...prev, [`delete_${userId}`]: false }));
    }
  };

  // Filter users
  const filteredUsers = users?.filter(user => {
    const matchesSearch = user?.username?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         user?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesStatus = statusFilter === 'all' || user?.status === statusFilter;
    const matchesRole = roleFilter === 'all' || user?.role === roleFilter;
    let matchesEngagement = engagementFilter === 'all';
    if (engagementFilter !== 'all' && user?.engagement) {
      const thisMonth = user?.engagement?.thisMonth || 0;
      if (engagementFilter === 'high') matchesEngagement = thisMonth >= 30;
      else if (engagementFilter === 'medium') matchesEngagement = thisMonth >= 10 && thisMonth < 30;
      else if (engagementFilter === 'low') matchesEngagement = thisMonth < 10;
    }
    return matchesSearch && matchesStatus && matchesRole && matchesEngagement;
  });

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev =>
      prev?.includes(userId) ? prev?.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers?.length === filteredUsers?.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers?.map(u => u?.id));
    }
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const handlePageSizeChange = (e) => { setPageSize(Number(e?.target?.value)); setCurrentPage(1); };

  // Load recent grants on mount
  useEffect(() => {
    loadRecentGrants();
  }, []);

  const loadRecentGrants = async () => {
    setGrantsLoading(true);
    try {
      const { data, error } = await supabase?.from('admin_talent_grants')?.select('*, admin:admin_id(email), recipient:user_id(email)')?.order('granted_at', { ascending: false })?.limit(20);
      if (!error) setRecentGrants(data || []);
    } catch (e) {
      console.error('Error loading grants:', e);
    } finally {
      setGrantsLoading(false);
    }
  };

  const handleAwardSearch = async (query) => {
    setAwardSearch(query);
    if (!query || query?.length < 2) { setAwardSearchResults([]); return; }
    setAwardSearchLoading(true);
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('id, username, email')?.or(`username.ilike.%${query}%,email.ilike.%${query}%`)?.limit(8);
      if (!error) setAwardSearchResults(data || []);
    } catch (e) {
      console.error('Error searching users:', e);
    } finally {
      setAwardSearchLoading(false);
    }
  };

  const handleAwardTalents = async () => {
    if (!selectedAwardUser) { setAwardError('Select a user first'); return; }
    if (goldAmount === 0 && silverAmount === 0) { setAwardError('Enter at least 1 gold or silver'); return; }

    setAwardLoading(true);
    setAwardError('');
    setAwardSuccess('');

    try {
      await supabase?.from('user_talent_balance')?.upsert(
        { user_id: selectedAwardUser?.id, gold_balance: 0, silver_balance: 0, total_gold_earned: 0, total_silver_earned: 0 },
        { onConflict: 'user_id', ignoreDuplicates: true }
      );

      const { data: bal, error: balError } = await supabase?.from('user_talent_balance')?.select('gold_balance, silver_balance, total_gold_earned, total_silver_earned')?.eq('user_id', selectedAwardUser?.id)?.maybeSingle();
      if (balError) throw balError;

      let newGold = (bal?.gold_balance || 0) + goldAmount;
      let newSilver = (bal?.silver_balance || 0) + silverAmount;
      let totalGoldEarned = (bal?.total_gold_earned || 0) + goldAmount;
      let totalSilverEarned = (bal?.total_silver_earned || 0) + silverAmount;

      const silverToGold = Math.floor(newSilver / 7);
      if (silverToGold > 0) {
        newGold += silverToGold;
        totalGoldEarned += silverToGold;
        newSilver = newSilver % 7;
      }

      const { error: updateError } = await supabase?.from('user_talent_balance')?.update({
        gold_balance: newGold, silver_balance: newSilver,
        total_gold_earned: totalGoldEarned, total_silver_earned: totalSilverEarned,
        updated_at: new Date()?.toISOString(),
      })?.eq('user_id', selectedAwardUser?.id);
      if (updateError) throw updateError;

      await supabase?.from('admin_talent_grants')?.insert({
        admin_id: adminUser?.id, user_id: selectedAwardUser?.id,
        gold_amount: goldAmount, silver_amount: silverAmount, note: awardNote || null,
      });

      try {
        await supabase?.functions?.invoke('send-award-email', {
          body: {
            to: selectedAwardUser?.email, displayName: selectedAwardUser?.username || selectedAwardUser?.email,
            awardName: 'Talent Award', awardDescription: awardNote || 'You have been awarded talents by an administrator.',
            awardIcon: '🪙', goldReward: goldAmount, silverReward: silverAmount,
          },
        });
      } catch (emailErr) {
        console.warn('Email notification failed (non-critical):', emailErr);
      }

      try {
        await supabase?.channel(`user:${selectedAwardUser?.id}:awards`)?.send({
          type: 'broadcast', event: 'talent_awarded',
          payload: { goldAmount, silverAmount, note: awardNote },
        });
      } catch (rtErr) {
        console.warn('Realtime notification failed (non-critical):', rtErr);
      }

      setAwardSuccess(`Awarded ${goldAmount > 0 ? `${goldAmount} gold` : ''}${goldAmount > 0 && silverAmount > 0 ? ' + ' : ''}${silverAmount > 0 ? `${silverAmount} silver` : ''} to ${selectedAwardUser?.username || selectedAwardUser?.email}`);
      setGoldAmount(0);
      setSilverAmount(0);
      setAwardNote('');
      setSelectedAwardUser(null);
      setAwardSearch('');
      setAwardSearchResults([]);
      await loadRecentGrants();
    } catch (err) {
      setAwardError(err?.message || 'Failed to award talents');
    } finally {
      setAwardLoading(false);
    }
  };

  // ── Quick Action Handlers ──────────────────────────────────────────────────

  const handleSendPasswordReset = async () => {
    if (!resetEmail?.trim()) {
      setResetMessage({ type: 'error', text: 'Please enter an email address.' });
      return;
    }
    setResetLoading(true);
    setResetMessage({ type: '', text: '' });
    try {
      const result = await sendPasswordResetEmail(resetEmail?.trim());
      if (result?.success) {
        setResetMessage({ type: 'success', text: `Password reset email sent to ${resetEmail}.` });
        setResetEmail('');
      } else {
        setResetMessage({ type: 'error', text: result?.error || 'Failed to send reset email.' });
      }
    } catch (err) {
      setResetMessage({ type: 'error', text: 'Failed to send reset email.' });
    } finally {
      setResetLoading(false);
    }
  };

  const handleSendWelcomeEmails = async () => {
    setShowOnboardingEmailModal(true);
  };

  const handleExportUserData = async () => {
    setQuickActionLoading('export');
    setQuickActionMessage({ type: '', text: '' });
    try {
      const result = await exportUserDataAsCSV();
      if (result?.success) {
        setQuickActionMessage({ type: 'success', text: `Exported ${result?.count} users to CSV.` });
      } else {
        setQuickActionMessage({ type: 'error', text: result?.error || 'Export failed.' });
      }
    } catch (err) {
      setQuickActionMessage({ type: 'error', text: 'Export failed.' });
    } finally {
      setQuickActionLoading('');
    }
  };

  const handleViewActivityLog = async () => {
    setShowActivityModal(true);
    setActivityLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('admin_audit_logs')
        ?.select('*')
        ?.order('created_at', { ascending: false })
        ?.limit(50);
      if (!error) setActivityLog(data || []);
    } catch (e) {
      console.error('Error loading activity log:', e);
    } finally {
      setActivityLoading(false);
    }
  };

  const handleManageRoles = () => {
    setShowManageRoles(true);
    setRoleUpdateMessage('');
    setEditingRole(null);
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    setRoleUpdateLoading(true);
    setRoleUpdateMessage('');
    try {
      const { error } = await supabase
        ?.from('user_profiles')
        ?.update({ role: newRole })
        ?.eq('id', userId);
      if (error) throw error;
      setUsers(prev => prev?.map(u => u?.id === userId ? { ...u, role: newRole } : u));
      setRoleUpdateMessage(`Role updated to "${newRole}" successfully.`);
      setEditingRole(null);
    } catch (err) {
      setRoleUpdateMessage(`Failed to update role: ${err?.message}`);
    } finally {
      setRoleUpdateLoading(false);
    }
  };

  const handleBulkOperation = async () => {
    if (!bulkAction) { setBulkMessage({ type: 'error', text: 'Select an action.' }); return; }
    if (selectedUsers?.length === 0) { setBulkMessage({ type: 'error', text: 'Select at least one user.' }); return; }

    const confirmed = window.confirm(
      `Apply "${bulkAction}" to ${selectedUsers?.length} selected user${selectedUsers?.length !== 1 ? 's' : ''}?`
    );
    if (!confirmed) return;

    setBulkLoading(true);
    setBulkMessage({ type: '', text: '' });

    try {
      if (bulkAction === 'activate' || bulkAction === 'deactivate') {
        const isActive = bulkAction === 'activate';
        const { error } = await supabase
          ?.from('user_profiles')
          ?.update({ is_active: isActive })
          ?.in('id', selectedUsers);
        if (error) throw error;
        setUsers(prev => prev?.map(u =>
          selectedUsers?.includes(u?.id) ? { ...u, isActive } : u
        ));
        setBulkMessage({ type: 'success', text: `${selectedUsers?.length} users ${isActive ? 'activated' : 'deactivated'}.` });
        setSelectedUsers([]);
      } else if (bulkAction === 'password_reset') {
        const targetUsers = users?.filter(u => selectedUsers?.includes(u?.id));
        let sent = 0;
        for (const u of targetUsers) {
          const result = await sendPasswordResetEmail(u?.email);
          if (result?.success) sent++;
        }
        setBulkMessage({ type: 'success', text: `Password reset emails sent to ${sent} users.` });
        setSelectedUsers([]);
      }
    } catch (err) {
      setBulkMessage({ type: 'error', text: err?.message || 'Bulk operation failed.' });
    } finally {
      setBulkLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <Link
                to="/admin-dashboard"
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-2"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Admin Dashboard</span>
              </Link>
              <h1 className="text-3xl font-bold text-foreground mb-2">User Management</h1>
              <p className="text-muted-foreground">Manage user accounts, permissions, and community engagement</p>
            </div>
            <div className="flex items-center space-x-3">
              <Button variant="outline" size="sm" onClick={handleExportUserData} disabled={quickActionLoading === 'export'}>
                {quickActionLoading === 'export' ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Exporting...</>
                ) : (
                  <><Download className="w-4 h-4 mr-2" />Export Data</>
                )}
              </Button>
              <Button variant="default" size="sm" onClick={() => setShowReportsModal(true)}>
                <FileText className="w-4 h-4 mr-2" />
                Generate Report
              </Button>
            </div>
          </div>
        </div>

        {/* Quick Action Feedback */}
        {quickActionMessage?.text && (
          <div className={`mb-4 flex items-center gap-2 px-4 py-3 rounded-lg border text-sm ${
            quickActionMessage?.type === 'success' ?'bg-success/10 border-success/20 text-success' :'bg-destructive/10 border-destructive/20 text-destructive'
          }`}>
            {quickActionMessage?.type === 'success'
              ? <CheckCircle className="w-4 h-4 shrink-0" />
              : <AlertCircle className="w-4 h-4 shrink-0" />}
            {quickActionMessage?.text}
            <button onClick={() => setQuickActionMessage({ type: '', text: '' })} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Analytics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Users</p>
                <p className="text-3xl font-bold text-foreground">{loading ? '...' : analytics?.totalUsers}</p>
                <p className="text-xs text-muted-foreground mt-1">All registered users</p>
              </div>
              <Users className="w-12 h-12 text-primary" />
            </div>
          </div>

          <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Active Users</p>
                <p className="text-3xl font-bold text-foreground">{loading ? '...' : analytics?.activeUsers}</p>
                <p className="text-xs text-muted-foreground mt-1">Active in last 30 days</p>
              </div>
              <Activity className="w-12 h-12 text-success" />
            </div>
          </div>

          <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">New This Month</p>
                <p className="text-3xl font-bold text-foreground">{loading ? '...' : analytics?.newUsersThisMonth}</p>
                <p className="text-xs text-muted-foreground mt-1">Joined this month</p>
              </div>
              <TrendingUp className="w-12 h-12 text-accent" />
            </div>
          </div>

          <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Avg Engagement</p>
                <p className="text-3xl font-bold text-foreground">{loading ? '...' : analytics?.averageEngagement}</p>
                <p className="text-xs text-muted-foreground mt-1">avg verses/user</p>
              </div>
              <TrendingUp className="w-12 h-12 text-warning" />
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
            <h3 className="text-lg font-semibold text-foreground mb-4">User Growth Trend</h3>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={userGrowthData?.length > 0 ? userGrowthData : []}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="month" stroke="var(--color-muted-foreground)" tick={{ fill: 'var(--color-muted-foreground)' }} />
                <YAxis stroke="var(--color-muted-foreground)" tick={{ fill: 'var(--color-muted-foreground)' }} />
                <Tooltip contentStyle={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', color: 'var(--color-foreground)' }} />
                <Line type="monotone" dataKey="users" stroke="var(--color-primary)" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
            <h3 className="text-lg font-semibold text-foreground mb-4">Role Distribution</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={permissionsData?.map(p => ({ name: p?.role, value: p?.users }))}
                  cx="50%" cy="50%" labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100)?.toFixed(0)}%`}
                  outerRadius={80} fill="#8884d8" dataKey="value"
                >
                  {permissionsData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={['var(--color-primary)', 'var(--color-success)', 'var(--color-warning)', 'var(--color-accent)']?.[index % 4]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', color: 'var(--color-foreground)' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* User Search & Filtering */}
        <div className="bg-card rounded-lg shadow-sm p-6 border border-border mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-foreground">User Search & Filtering</h2>
            <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}>
              <Filter className="w-4 h-4 mr-2" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
          </div>

          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                placeholder="Search by username or email..."
                className="w-full pl-10 pr-4 py-2 border border-border rounded-md bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
              />
            </div>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 p-4 bg-muted/30 rounded-md">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Role</label>
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="all">All Roles</option>
                  <option value="standard">Standard</option>
                  <option value="premium">Premium</option>
                  <option value="beta_tester">Beta Tester</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Engagement</label>
                <select
                  value={engagementFilter}
                  onChange={(e) => setEngagementFilter(e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="all">All Levels</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
            </div>
          )}

          <div className="text-sm text-muted-foreground mb-2">
            Showing {filteredUsers?.length} of {users?.length} users
            {selectedUsers?.length > 0 && ` (${selectedUsers?.length} selected)`}
          </div>
        </div>

        {/* User Account Management Table */}
        <div className="bg-card rounded-lg shadow-sm border border-border mb-8 overflow-hidden">
          <div className="p-6 border-b border-border">
            <h2 className="text-lg font-semibold text-foreground">User Account Management</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedUsers?.length === filteredUsers?.length && filteredUsers?.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-border text-primary focus:ring-ring"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Account Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Engagement</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Stats</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Last Active</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredUsers?.map((user) => (
                  <tr key={user?.id} className={`hover:bg-muted/30 transition-colors ${!user?.isActive ? 'opacity-50 bg-muted/20' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedUsers?.includes(user?.id)}
                        onChange={() => handleSelectUser(user?.id)}
                        className="rounded border-border text-primary focus:ring-ring"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
                          <span className="text-primary font-semibold">{user?.username?.charAt(0)?.toUpperCase()}</span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-foreground">{user?.username}</div>
                          <div className="text-sm text-muted-foreground">{user?.email}</div>
                          <div className="text-xs text-muted-foreground mt-1">Joined {user?.joinDate}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user?.isActive ? (
                        <span className="flex items-center px-3 py-1 text-xs font-semibold rounded-full bg-success/15 text-success">
                          <CheckCircle className="w-3 h-3 mr-1" />Active
                        </span>
                      ) : (
                        <span className="flex items-center px-3 py-1 text-xs font-semibold rounded-full bg-muted text-muted-foreground">
                          <UserX className="w-3 h-3 mr-1" />Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-accent/15 text-accent">
                        {user?.role?.charAt(0)?.toUpperCase() + user?.role?.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-foreground">
                        <div className="font-semibold">{user?.engagement?.thisMonth} this month</div>
                        <div className="text-xs text-muted-foreground">{user?.engagement?.average} average</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-foreground">
                        <div>{user?.versesMemorized} verses</div>
                        <div className="text-xs text-muted-foreground">{user?.streak} day streak</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{user?.lastActive}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-2">
                        <Button
                          variant={user?.isActive ? 'outline' : 'default'}
                          size="sm"
                          onClick={() => handleToggleUserStatus(user?.id, user?.isActive, user?.username)}
                          disabled={actionLoading?.[user?.id]}
                          className={user?.isActive ? 'text-destructive border-destructive/30 hover:bg-destructive/10' : 'bg-success hover:bg-success/90 text-success-foreground'}
                        >
                          {actionLoading?.[user?.id] ? (
                            <span className="flex items-center">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />Processing...
                            </span>
                          ) : (
                            <span className="flex items-center">
                              {user?.isActive ? (
                                <><UserX className="w-3 h-3 mr-1" />Deactivate</>
                              ) : (
                                <><UserPlus className="w-3 h-3 mr-1" />Activate</>
                              )}
                            </span>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteUser(user?.id, user?.username)}
                          disabled={actionLoading?.[`delete_${user?.id}`]}
                          className="text-destructive border-destructive/30 hover:bg-destructive/10"
                          title="Delete user permanently"
                        >
                          {actionLoading?.[`delete_${user?.id}`] ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
                          ) : (
                            <Trash2 className="w-3 h-3" />
                          )}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {userLoading && (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <p className="mt-2 text-sm text-muted-foreground">Loading users...</p>
              </div>
            )}

            {!userLoading && filteredUsers?.length === 0 && (
              <div className="text-center py-12">
                <Users className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-2 text-sm font-medium text-foreground">No users found</h3>
                <p className="mt-1 text-sm text-muted-foreground">Try adjusting your search or filter criteria.</p>
              </div>
            )}
          </div>

          {!userLoading && filteredUsers?.length > 0 && (
            <div className="bg-card px-6 py-4 border-t border-border flex items-center justify-between">
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    Showing <span className="font-medium">{((currentPage - 1) * pagination?.pageSize) + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(currentPage * pagination?.pageSize, pagination?.totalUsers)}</span>{' '}
                    of <span className="font-medium">{pagination?.totalUsers}</span> users
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-border bg-card text-sm font-medium text-muted-foreground hover:bg-muted/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Previous
                    </button>
                    {[...Array(pagination?.totalPages)]?.map((_, idx) => {
                      const pageNum = idx + 1;
                      if (pageNum === 1 || pageNum === pagination?.totalPages || (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)) {
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium transition-colors ${
                              pageNum === currentPage
                                ? 'z-10 bg-primary/10 border-primary text-primary' :'bg-card border-border text-muted-foreground hover:bg-muted/50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                        return (
                          <span key={pageNum} className="relative inline-flex items-center px-4 py-2 border border-border bg-card text-sm font-medium text-muted-foreground">...</span>
                        );
                      }
                      return null;
                    })}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === pagination?.totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-border bg-card text-sm font-medium text-muted-foreground hover:bg-muted/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Award Talents Section */}
        <div className="bg-card rounded-lg shadow-sm p-6 border border-border mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-warning/10 p-2 rounded-full">
              <Award className="w-5 h-5 text-warning" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Award Talents</h2>
              <p className="text-sm text-muted-foreground">Manually grant gold or silver talents to a user</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Award Form */}
            <div className="space-y-4">
              <div className="relative">
                <label className="block text-sm font-medium text-foreground mb-1">Search User</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="text"
                    value={awardSearch}
                    onChange={(e) => handleAwardSearch(e?.target?.value)}
                    placeholder="Search by name or email..."
                    className="w-full pl-9 pr-4 py-2 border border-border rounded-md bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                  />
                </div>
                {awardSearchResults?.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-card border border-border rounded-lg shadow-lg overflow-hidden">
                    {awardSearchResults?.map(u => (
                      <button
                        key={u?.id}
                        onClick={() => { setSelectedAwardUser(u); setAwardSearch(u?.username || u?.email); setAwardSearchResults([]); }}
                        className="w-full text-left px-4 py-2.5 hover:bg-muted/50 transition-colors border-b border-border last:border-0"
                      >
                        <p className="text-sm font-medium text-foreground">{u?.username || 'No username'}</p>
                        <p className="text-xs text-muted-foreground">{u?.email}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {selectedAwardUser && (
                <div className="flex items-center gap-2 px-3 py-2 bg-primary/10 border border-primary/20 rounded-lg">
                  <div className="w-7 h-7 bg-primary/20 rounded-full flex items-center justify-center shrink-0">
                    <span className="text-primary text-xs font-bold">
                      {(selectedAwardUser?.username || selectedAwardUser?.email)?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{selectedAwardUser?.username || 'No username'}</p>
                    <p className="text-xs text-muted-foreground truncate">{selectedAwardUser?.email}</p>
                  </div>
                  <button onClick={() => { setSelectedAwardUser(null); setAwardSearch(''); }} className="text-muted-foreground hover:text-foreground">
                    <UserX className="w-4 h-4" />
                  </button>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1 flex items-center gap-1">
                    <span>🪙</span> Gold (0–100)
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={goldAmount}
                    onChange={(e) => setGoldAmount(Math.max(0, Math.min(100, parseInt(e?.target?.value) || 0)))}
                    className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1 flex items-center gap-1">
                    <span>🥈</span> Silver (0–6)
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={6}
                    value={silverAmount}
                    onChange={(e) => setSilverAmount(Math.max(0, Math.min(6, parseInt(e?.target?.value) || 0)))}
                    className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                  />
                  <p className="text-xs text-muted-foreground mt-1">7 silver auto-converts to 1 gold</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Note (optional)</label>
                <input
                  type="text"
                  value={awardNote}
                  onChange={(e) => setAwardNote(e?.target?.value)}
                  placeholder="e.g. Contest winner, special recognition..."
                  className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                />
              </div>

              {awardError && (
                <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  {awardError}
                </div>
              )}
              {awardSuccess && (
                <div className="flex items-center gap-2 text-sm text-success bg-success/10 border border-success/20 rounded-lg px-3 py-2">
                  <CheckCircle className="w-4 h-4 shrink-0" />
                  {awardSuccess}
                </div>
              )}

              <Button
                onClick={handleAwardTalents}
                disabled={awardLoading || !selectedAwardUser || (goldAmount === 0 && silverAmount === 0)}
                className="w-full flex items-center justify-center gap-2"
              >
                {awardLoading ? (
                  <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />Awarding...</>
                ) : (
                  <><Award className="w-4 h-4" />Award Talents</>
                )}
              </Button>
            </div>

            {/* Recent Grants History */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3">Recent Grants</h3>
              {grantsLoading ? (
                <div className="text-center py-6">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary mx-auto" />
                </div>
              ) : recentGrants?.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">No grants yet</p>
              ) : (
                <div className="space-y-2 max-h-[320px] overflow-y-auto">
                  {recentGrants?.map(grant => (
                    <div key={grant?.id} className="flex items-start gap-3 p-3 bg-muted/30 border border-border rounded-lg">
                      <div className="shrink-0 mt-0.5">
                        <Coins className="w-4 h-4 text-warning" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-medium text-foreground truncate">
                            {grant?.recipient?.email || grant?.user_id}
                          </span>
                          <div className="flex items-center gap-1">
                            {grant?.gold_amount > 0 && (
                              <span className="text-xs bg-warning/15 text-warning px-1.5 py-0.5 rounded">
                                +{grant?.gold_amount}🪙
                              </span>
                            )}
                            {grant?.silver_amount > 0 && (
                              <span className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded">
                                +{grant?.silver_amount}🥈
                              </span>
                            )}
                          </div>
                        </div>
                        {grant?.note && (
                          <p className="text-xs text-muted-foreground mt-0.5 truncate">{grant?.note}</p>
                        )}
                        <p className="text-xs text-muted-foreground/60 mt-0.5">
                          {new Date(grant.granted_at)?.toLocaleDateString()} · by {grant?.admin?.email || 'admin'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-card rounded-lg shadow-sm p-6 border border-border mb-8">
          <h2 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <button
              onClick={() => { setShowPasswordResetModal(true); setResetMessage({ type: '', text: '' }); setResetEmail(''); }}
              className="flex items-center p-4 border border-border rounded-lg hover:border-primary/50 hover:bg-primary/5 transition-all duration-200 text-left group"
            >
              <Mail className="w-8 h-8 text-primary mr-3 flex-shrink-0 group-hover:scale-110 transition-transform" />
              <div>
                <h3 className="font-medium text-foreground">Send Password Reset</h3>
                <p className="text-sm text-muted-foreground">Send reset email to a user</p>
              </div>
            </button>

            <button
              onClick={handleSendWelcomeEmails}
              disabled={quickActionLoading === 'welcome'}
              className="flex items-center p-4 border border-border rounded-lg hover:border-success/50 hover:bg-success/5 transition-all duration-200 text-left group disabled:opacity-60"
            >
              {quickActionLoading === 'welcome' ? (
                <RefreshCw className="w-8 h-8 text-success mr-3 flex-shrink-0 animate-spin" />
              ) : (
                <UserCheck className="w-8 h-8 text-success mr-3 flex-shrink-0 group-hover:scale-110 transition-transform" />
              )}
              <div>
                <h3 className="font-medium text-foreground">Welcome Emails</h3>
                <p className="text-sm text-muted-foreground">
                  {selectedUsers?.length > 0 ? `Send to ${selectedUsers?.length} selected` : 'Send onboarding emails'}
                </p>
              </div>
            </button>

            <button
              onClick={handleExportUserData}
              disabled={quickActionLoading === 'export'}
              className="flex items-center p-4 border border-border rounded-lg hover:border-accent/50 hover:bg-accent/5 transition-all duration-200 text-left group disabled:opacity-60"
            >
              {quickActionLoading === 'export' ? (
                <RefreshCw className="w-8 h-8 text-accent mr-3 flex-shrink-0 animate-spin" />
              ) : (
                <Download className="w-8 h-8 text-accent mr-3 flex-shrink-0 group-hover:scale-110 transition-transform" />
              )}
              <div>
                <h3 className="font-medium text-foreground">Export User Data</h3>
                <p className="text-sm text-muted-foreground">Download all users as CSV</p>
              </div>
            </button>

            <button
              onClick={() => setShowReportsModal(true)}
              className="flex items-center p-4 border border-border rounded-lg hover:border-warning/50 hover:bg-warning/5 transition-all duration-200 text-left group"
            >
              <FileText className="w-8 h-8 text-warning mr-3 flex-shrink-0 group-hover:scale-110 transition-transform" />
              <div>
                <h3 className="font-medium text-foreground">Activity Reports</h3>
                <p className="text-sm text-muted-foreground">View platform statistics</p>
              </div>
            </button>

            <button
              onClick={() => { setShowBulkModal(true); setBulkMessage({ type: '', text: '' }); setBulkAction(''); }}
              className="flex items-center p-4 border border-border rounded-lg hover:border-primary/50 hover:bg-primary/5 transition-all duration-200 text-left group"
            >
              <Users className="w-8 h-8 text-primary mr-3 flex-shrink-0 group-hover:scale-110 transition-transform" />
              <div>
                <h3 className="font-medium text-foreground">Bulk Operations</h3>
                <p className="text-sm text-muted-foreground">
                  {selectedUsers?.length > 0 ? `${selectedUsers?.length} users selected` : 'Bulk email & status changes'}
                </p>
              </div>
            </button>
          </div>
        </div>

        {/* Permissions & Roles */}
        <div className="bg-card rounded-lg shadow-sm p-6 border border-border mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-foreground">Permissions & Roles</h2>
            <Button variant="outline" size="sm" onClick={handleManageRoles}>
              <Shield className="w-4 h-4 mr-2" />Manage Roles
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Users</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">Import</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">Export</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">Moderate</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">Admin Access</th>
                </tr>
              </thead>
              <tbody className="bg-card divide-y divide-border">
                {communityLoading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center">
                      <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                      <p className="mt-2 text-sm text-muted-foreground">Loading roles...</p>
                    </td>
                  </tr>
                ) : permissionsData?.map((perm) => (
                  <tr key={perm?.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-6 py-4"><span className="text-sm font-medium text-foreground">{perm?.role}</span></td>
                    <td className="px-6 py-4"><span className="text-sm text-muted-foreground">{perm?.users} users</span></td>
                    <td className="px-6 py-4 text-center">
                      {perm?.canImport ? <CheckCircle className="w-5 h-5 text-success mx-auto" /> : <AlertCircle className="w-5 h-5 text-muted-foreground/40 mx-auto" />}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {perm?.canExport ? <CheckCircle className="w-5 h-5 text-success mx-auto" /> : <AlertCircle className="w-5 h-5 text-muted-foreground/40 mx-auto" />}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {perm?.canModerate ? <CheckCircle className="w-5 h-5 text-success mx-auto" /> : <AlertCircle className="w-5 h-5 text-muted-foreground/40 mx-auto" />}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {perm?.canAccessAdmin ? <CheckCircle className="w-5 h-5 text-success mx-auto" /> : <AlertCircle className="w-5 h-5 text-muted-foreground/40 mx-auto" />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Community Management */}
        <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-foreground">Community Management</h2>
            <Button variant="outline" size="sm" onClick={handleViewActivityLog}>
              <MessageSquare className="w-4 h-4 mr-2" />View All Activity
            </Button>
          </div>
          {communityLoading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              <p className="mt-2 text-sm text-muted-foreground">Loading community data...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="p-4 bg-primary/10 rounded-lg border border-primary/20">
                <div className="flex items-center justify-between mb-2">
                  <MessageSquare className="w-8 h-8 text-primary" />
                  <span className="text-2xl font-bold text-foreground">{communityStats?.totalReflections?.toLocaleString()}</span>
                </div>
                <p className="text-sm text-primary font-medium">Total Reflections</p>
                <p className="text-xs text-muted-foreground mt-1">User-generated content</p>
              </div>
              <div className="p-4 bg-success/10 rounded-lg border border-success/20">
                <div className="flex items-center justify-between mb-2">
                  <Users className="w-8 h-8 text-success" />
                  <span className="text-2xl font-bold text-foreground">{communityStats?.publicProfiles?.toLocaleString()}</span>
                </div>
                <p className="text-sm text-success font-medium">Public Profiles</p>
                <p className="text-xs text-muted-foreground mt-1">Community visibility</p>
              </div>
              <div className="p-4 bg-destructive/10 rounded-lg border border-destructive/20">
                <div className="flex items-center justify-between mb-2">
                  <AlertCircle className="w-8 h-8 text-destructive" />
                  <span className="text-2xl font-bold text-foreground">{communityStats?.flaggedContent?.toLocaleString()}</span>
                </div>
                <p className="text-sm text-destructive font-medium">Flagged Content</p>
                <p className="text-xs text-muted-foreground mt-1">Requires review</p>
              </div>
              <div className="p-4 bg-accent/10 rounded-lg border border-accent/20">
                <div className="flex items-center justify-between mb-2">
                  <Shield className="w-8 h-8 text-accent" />
                  <span className="text-2xl font-bold text-foreground">{communityStats?.moderationActions?.toLocaleString()}</span>
                </div>
                <p className="text-sm text-accent font-medium">Moderation Actions</p>
                <p className="text-xs text-muted-foreground mt-1">This month</p>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* ── Password Reset Modal ─────────────────────────────────────────────── */}
      {showPasswordResetModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card rounded-xl shadow-xl border border-border w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold text-foreground">Send Password Reset</h2>
              </div>
              <button onClick={() => setShowPasswordResetModal(false)} className="text-muted-foreground hover:text-foreground">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-muted-foreground">Enter the user's email address to send a password reset link.</p>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Email Address</label>
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e?.target?.value)}
                  placeholder="user@example.com"
                  className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  onKeyDown={(e) => e?.key === 'Enter' && handleSendPasswordReset()}
                />
              </div>
              {resetMessage?.text && (
                <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg border ${
                  resetMessage?.type === 'success' ?'bg-success/10 border-success/20 text-success' :'bg-destructive/10 border-destructive/20 text-destructive'
                }`}>
                  {resetMessage?.type === 'success' ? <CheckCircle className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
                  {resetMessage?.text}
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowPasswordResetModal(false)}>Cancel</Button>
                <Button className="flex-1" onClick={handleSendPasswordReset} disabled={resetLoading}>
                  {resetLoading ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Sending...</> : <><Mail className="w-4 h-4 mr-2" />Send Reset</>}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ── Bulk Operations Modal ────────────────────────────────────────────── */}
      {showBulkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card rounded-xl shadow-xl border border-border w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold text-foreground">Bulk Operations</h2>
              </div>
              <button onClick={() => setShowBulkModal(false)} className="text-muted-foreground hover:text-foreground">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {selectedUsers?.length === 0 ? (
                <div className="flex items-center gap-2 text-sm text-warning bg-warning/10 border border-warning/20 rounded-lg px-3 py-2">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  No users selected. Check the boxes in the user table first.
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Apply action to <span className="font-semibold text-foreground">{selectedUsers?.length} selected user{selectedUsers?.length !== 1 ? 's' : ''}</span>.
                </p>
              )}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Select Action</label>
                <select
                  value={bulkAction}
                  onChange={(e) => setBulkAction(e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">Choose an action...</option>
                  <option value="activate">Activate Accounts</option>
                  <option value="deactivate">Deactivate Accounts</option>
                  <option value="password_reset">Send Password Reset Emails</option>
                </select>
              </div>
              {bulkMessage?.text && (
                <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg border ${
                  bulkMessage?.type === 'success' ?'bg-success/10 border-success/20 text-success' :'bg-destructive/10 border-destructive/20 text-destructive'
                }`}>
                  {bulkMessage?.type === 'success' ? <CheckCircle className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
                  {bulkMessage?.text}
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowBulkModal(false)}>Close</Button>
                <Button
                  className="flex-1"
                  onClick={handleBulkOperation}
                  disabled={bulkLoading || !bulkAction || selectedUsers?.length === 0}
                >
                  {bulkLoading ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Processing...</> : 'Apply Action'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ── Manage Roles Modal ───────────────────────────────────────────────── */}
      {showManageRoles && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card rounded-xl shadow-xl border border-border w-full max-w-2xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold text-foreground">Manage User Roles</h2>
              </div>
              <button onClick={() => setShowManageRoles(false)} className="text-muted-foreground hover:text-foreground">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <p className="text-sm text-muted-foreground mb-4">
                Change a user's role by searching and selecting them below. Available roles: <span className="font-medium text-foreground">user, standard, premium, beta_tester, admin</span>.
              </p>
              {roleUpdateMessage && (
                <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg border mb-4 ${
                  roleUpdateMessage?.includes('success') || roleUpdateMessage?.includes('updated')
                    ? 'bg-success/10 border-success/20 text-success' :'bg-destructive/10 border-destructive/20 text-destructive'
                }`}>
                  <CheckCircle className="w-4 h-4 shrink-0" />{roleUpdateMessage}
                </div>
              )}
              <div className="space-y-2">
                {users?.slice(0, 20)?.map(u => (
                  <div key={u?.id} className="flex items-center justify-between p-3 bg-muted/30 border border-border rounded-lg">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                        <span className="text-primary text-xs font-bold">{u?.username?.charAt(0)?.toUpperCase()}</span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{u?.username}</p>
                        <p className="text-xs text-muted-foreground truncate">{u?.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-3">
                      {editingRole === u?.id ? (
                        <>
                          <select
                            defaultValue={u?.role}
                            onChange={(e) => handleUpdateUserRole(u?.id, e?.target?.value)}
                            disabled={roleUpdateLoading}
                            className="text-xs px-2 py-1 border border-border rounded bg-input text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                          >
                            <option value="user">User</option>
                            <option value="standard">Standard</option>
                            <option value="premium">Premium</option>
                            <option value="beta_tester">Beta Tester</option>
                            <option value="admin">Admin</option>
                          </select>
                          <button onClick={() => setEditingRole(null)} className="text-muted-foreground hover:text-foreground">
                            <X className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <span className="text-xs px-2 py-1 bg-accent/15 text-accent rounded-full font-medium">
                            {u?.role}
                          </span>
                          <button
                            onClick={() => setEditingRole(u?.id)}
                            className="text-xs text-primary hover:underline flex items-center gap-1"
                          >
                            Edit <ChevronRight className="w-3 h-3" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
                {users?.length > 20 && (
                  <p className="text-xs text-muted-foreground text-center pt-2">
                    Showing first 20 users. Use the search filter above to find specific users.
                  </p>
                )}
              </div>
            </div>
            <div className="p-4 border-t border-border">
              <Button variant="outline" className="w-full" onClick={() => setShowManageRoles(false)}>Close</Button>
            </div>
          </div>
        </div>
      )}
      {/* ── View All Activity Modal ──────────────────────────────────────────── */}
      {showActivityModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card rounded-xl shadow-xl border border-border w-full max-w-3xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center gap-3">
                <Activity className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold text-foreground">All Activity Log</h2>
              </div>
              <button onClick={() => setShowActivityModal(false)} className="text-muted-foreground hover:text-foreground">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {activityLoading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
                  <p className="mt-2 text-sm text-muted-foreground">Loading activity...</p>
                </div>
              ) : activityLog?.length === 0 ? (
                <div className="text-center py-12">
                  <Activity className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">No activity logs found.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {activityLog?.map((log, idx) => (
                    <div key={log?.id || idx} className="flex items-start gap-3 p-3 bg-muted/30 border border-border rounded-lg">
                      <div className="w-2 h-2 rounded-full bg-primary mt-2 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <span className="text-sm font-medium text-foreground">
                            {log?.action || log?.event_type || 'Activity'}
                          </span>
                          <span className="text-xs text-muted-foreground shrink-0">
                            {log?.created_at ? new Date(log.created_at)?.toLocaleString() : ''}
                          </span>
                        </div>
                        {(log?.details || log?.description) && (
                          <p className="text-xs text-muted-foreground mt-0.5 truncate">
                            {typeof (log?.details || log?.description) === 'object'
                              ? JSON.stringify(log?.details || log?.description)
                              : (log?.details || log?.description)}
                          </p>
                        )}
                        {log?.user_id && (
                          <p className="text-xs text-muted-foreground/60 mt-0.5">User: {log?.user_id}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-border">
              <Button variant="outline" className="w-full" onClick={() => setShowActivityModal(false)}>Close</Button>
            </div>
          </div>
        </div>
      )}
      {/* ── Activity Reports Modal ───────────────────────────────────────────── */}
      {showReportsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card rounded-xl shadow-xl border border-border w-full max-w-lg">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-warning" />
                <h2 className="text-lg font-semibold text-foreground">Activity Reports</h2>
              </div>
              <button onClick={() => setShowReportsModal(false)} className="text-muted-foreground hover:text-foreground">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-primary/10 rounded-lg border border-primary/20 text-center">
                  <p className="text-2xl font-bold text-foreground">{analytics?.totalUsers}</p>
                  <p className="text-xs text-muted-foreground mt-1">Total Users</p>
                </div>
                <div className="p-4 bg-success/10 rounded-lg border border-success/20 text-center">
                  <p className="text-2xl font-bold text-foreground">{analytics?.activeUsers}</p>
                  <p className="text-xs text-muted-foreground mt-1">Active (30 days)</p>
                </div>
                <div className="p-4 bg-accent/10 rounded-lg border border-accent/20 text-center">
                  <p className="text-2xl font-bold text-foreground">{analytics?.newUsersThisMonth}</p>
                  <p className="text-xs text-muted-foreground mt-1">New This Month</p>
                </div>
                <div className="p-4 bg-warning/10 rounded-lg border border-warning/20 text-center">
                  <p className="text-2xl font-bold text-foreground">{communityStats?.totalReflections}</p>
                  <p className="text-xs text-muted-foreground mt-1">Total Reflections</p>
                </div>
              </div>
              <div className="pt-2 flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setShowReportsModal(false)}>Close</Button>
                <Button className="flex-1" onClick={() => { handleExportUserData(); setShowReportsModal(false); }}>
                  <Download className="w-4 h-4 mr-2" />Export CSV
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ── Onboarding Email Modal ─────────────────────────────────────────── */}
      <OnboardingEmailModal
        isOpen={showOnboardingEmailModal}
        onClose={() => setShowOnboardingEmailModal(false)}
        users={users}
        selectedUsers={selectedUsers}
      />
      {/* ── Delete User Confirmation Modal ────────────────────────────────── */}
      {deleteConfirm?.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-card rounded-xl shadow-2xl border border-border w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-destructive/10 rounded-full flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-destructive" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">
                    {deleteConfirm?.step === 1 ? 'Delete User Account' : 'Final Confirmation'}
                  </h2>
                  <p className="text-xs text-muted-foreground">
                    Step {deleteConfirm?.step} of 2
                  </p>
                </div>
              </div>
              <button
                onClick={() => setDeleteConfirm({ show: false, userId: null, username: '', step: 1 })}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {deleteConfirm?.step === 1 ? (
                <>
                  <div className="flex items-start gap-3 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-destructive mb-1">This action is permanent and cannot be undone.</p>
                      <p className="text-sm text-muted-foreground">
                        You are about to permanently delete the account for{' '}
                        <span className="font-semibold text-foreground">"{deleteConfirm?.username}"</span>.
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <p className="font-medium text-foreground">The following data will be permanently removed:</p>
                    <ul className="space-y-1 ml-4 list-disc">
                      <li>User account and login credentials</li>
                      <li>All memorized verses and review history</li>
                      <li>Verse reflections and highlights</li>
                      <li>Reading plan progress</li>
                      <li>Study notes and activity logs</li>
                    </ul>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-start gap-3 p-4 bg-destructive/15 border border-destructive/30 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-destructive mb-1">Are you absolutely sure?</p>
                      <p className="text-sm text-muted-foreground">
                        Deleting <span className="font-semibold text-foreground">"{deleteConfirm?.username}"</span> will erase all their data from the system. This cannot be recovered.
                      </p>
                    </div>
                  </div>
                </>
              )}

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setDeleteConfirm({ show: false, userId: null, username: '', step: 1 })}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-destructive hover:bg-destructive/90 text-destructive-foreground border-destructive"
                  onClick={handleConfirmDelete}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {deleteConfirm?.step === 1 ? 'Continue' : 'Yes, Delete Permanently'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;